import entries from 'lodash/entries';
import flatMap from 'lodash/flatMap';
import flattenDeep from 'lodash/flattenDeep';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import keys from 'lodash/keys';
import { DF_KEYWORD_TRANSFORM } from '../constants';

export const pairValuesWithLabels = ({ labels, values }, source, cb) =>
  values.reduce((acc, value, index) => {
    let label = null;

    if (isPlainObject(labels)) {
      label = labels[value];
    } else if (isArray(labels)) {
      label = labels[index];
    }

    return cb(acc, label, value);
  }, source);

export const getInitialFormValues = node => {
  if (typeof node.default !== 'undefined') return node.default;

  if (node.type === 'array') {
    return [getInitialFormValues(node.items)];
  }

  if (node.type === 'object') {
    const propertyNames = keys(node.properties);
    let initial = {};

    return propertyNames.reduce((acc, key) => {
      const value = getInitialFormValues(node.properties[key]);
      if (typeof value !== 'undefined') return { ...acc, [key]: value };
      return acc;
    }, initial);
  }
};

export const transformEnums = (enums, mapping) =>
  flatMap(mapping, (inputName, index) => {
    if (inputName === '...') {
      return () => {
        const lastIndex = mapping.length - 1;
        const left = enums.indexOf(mapping[index - 1]);
        const right = enums.indexOf(mapping[index + 1]);
        const leftInvalid = left === -1 && index !== 0;
        const rightInvalid = right === -1 && index !== lastIndex;

        if (leftInvalid && rightInvalid) return [];

        return enums
          .slice(
            index === 0 ? 0 : left + 1,
            right === -1 || right < index ? enums.length : right
          )
          .filter(value => !mapping.includes(value));
      };
    }

    return inputName;
  }).reduce((acc, value) => {
    const dedupeValues = (dedupedAcc, value) => {
      if (dedupedAcc.includes(value)) {
        const index = dedupedAcc.indexOf(value);

        return [
          ...dedupedAcc.slice(0, index),
          ...dedupedAcc.slice(index + 1),
          value,
        ];
      }

      return [...dedupedAcc, value];
    };

    if (typeof value === 'function') {
      return value().reduce(
        (dedupedAcc, dedupeValue) => dedupeValues(dedupedAcc, dedupeValue),
        acc
      );
    }

    return dedupeValues(acc, value);
  }, []);

export const transformEnumsAtSchemaNode = ({ enum: enums, ...schemaNode }) => {
  const mapping = schemaNode[DF_KEYWORD_TRANSFORM];

  if (mapping) return transformEnums(enums, mapping);

  return enums;
};

export const getFieldNamesFromFormStructure = formStructure =>
  entries(formStructure).reduce(
    (acc, props, index) => [
      ...acc,
      ...(function recurse(acc, [key, value]) {
        if (isPlainObject(value)) {
          return flattenDeep(
            entries(value).map(props => recurse([...acc, key], props))
          );
        }

        if (isArray(value)) {
          return flattenDeep(
            value.map((value, index) => recurse([...acc, key], [index, value]))
          );
        }

        return [
          [...acc, key].reduce((acc, part, index) => {
            if (typeof part === 'number') {
              return `${acc}[${part}]`;
            }

            return (index ? acc + '.' : '') + part;
          }, ''),
        ];
      })(acc[index] || [], props),
    ],
    []
  );
